<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MetaBusinessAgentAPI from 'dashboard/api/metaBusinessAgent';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ResourceList from './ResourceList.vue';

const props = defineProps({
  hookId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const T = key => t(`INTEGRATION_SETTINGS.META_BUSINESS_AGENT.UI_SKILLS.${key}`);

const COMPONENT_TYPES = [
  'carousel_quick_reply',
  'carousel_url',
  'cta_url',
  'image',
  'interactive_list',
  'location',
  'location_request',
];

const uiSkills = ref([]);
const showForm = ref(false);
const title = ref('');
const componentType = ref(COMPONENT_TYPES[0]);
const instruction = ref('');
const pendingDelete = ref(null);
const showDeleteModal = ref(false);

const fetchUiSkills = async () => {
  try {
    const { data } = await MetaBusinessAgentAPI.listUiSkills(props.hookId);
    uiSkills.value = data.data || [];
  } catch (error) {
    useAlert(T('API.FETCH_ERROR'));
  }
};

onMounted(fetchUiSkills);

const resetForm = () => {
  title.value = '';
  componentType.value = COMPONENT_TYPES[0];
  instruction.value = '';
  showForm.value = false;
};

const createUiSkill = async () => {
  try {
    await MetaBusinessAgentAPI.createUiSkill(props.hookId, {
      title: title.value,
      component_type: componentType.value,
      instruction: instruction.value,
      status: 'enabled',
    });
    useAlert(T('API.CREATE_SUCCESS'));
    resetForm();
    fetchUiSkills();
  } catch (error) {
    useAlert(T('API.CREATE_ERROR'));
  }
};

const confirmDelete = item => {
  pendingDelete.value = item;
  showDeleteModal.value = true;
};

const deleteUiSkill = async () => {
  try {
    await MetaBusinessAgentAPI.deleteUiSkill(
      props.hookId,
      pendingDelete.value.id
    );
    useAlert(T('API.DELETE_SUCCESS'));
    fetchUiSkills();
  } catch (error) {
    useAlert(T('API.DELETE_ERROR'));
  } finally {
    showDeleteModal.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex justify-end">
      <Button :label="T('ADD_BUTTON')" @click="showForm = true" />
    </div>
    <div
      v-if="showForm"
      class="flex flex-col gap-3 p-4 rounded-lg bg-n-alpha-1"
    >
      <Input v-model="title" :label="T('TITLE_LABEL')" />
      <div>
        <label class="text-sm text-n-slate-11">{{
          T('COMPONENT_TYPE_LABEL')
        }}</label>
        <select v-model="componentType">
          <option v-for="type in COMPONENT_TYPES" :key="type" :value="type">
            {{ type }}
          </option>
        </select>
      </div>
      <Input v-model="instruction" :label="T('INSTRUCTION_LABEL')" />
      <div class="flex justify-end gap-2">
        <Button
          slate
          faded
          :label="$t('INTEGRATION_APPS.ADD.FORM.CANCEL')"
          @click="resetForm"
        />
        <Button
          :label="$t('INTEGRATION_APPS.ADD.FORM.SUBMIT')"
          @click="createUiSkill"
        />
      </div>
    </div>
    <ResourceList
      :items="uiSkills"
      :empty-state="T('EMPTY_STATE')"
      @delete="confirmDelete"
    >
      <template #default="{ item }">
        <p class="font-medium text-n-slate-12 m-0">{{ item.title }}</p>
        <p class="text-sm text-n-slate-11 m-0">
          {{ item.component_type }} · {{ item.status }}
        </p>
      </template>
    </ResourceList>

    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-close="() => (showDeleteModal = false)"
      :on-confirm="deleteUiSkill"
      :title="T('DELETE_CONFIRM_TITLE')"
      :message="T('DELETE_CONFIRM_MESSAGE')"
      :confirm-text="$t('INTEGRATION_APPS.DELETE.CONFIRM_BUTTON_TEXT.INBOX')"
      :reject-text="$t('INTEGRATION_APPS.DELETE.CANCEL_BUTTON_TEXT')"
    />
  </div>
</template>
